import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ArticleIcon from "@mui/icons-material/Article";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";

const themeColor = "#286a94";

const Resources = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />

      {/* HERO SECTION */}
      <Box
        sx={{
          width: "100%",
          py: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, #1b3d57, #286a94)`,
          color: "white",
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, fontSize: { xs: "28px", md: "40px" } }}
        >
          Knowledge Hub & Learning Space
        </Typography>

        <Typography sx={{ maxWidth: "750px", mx: "auto", mt: 2, opacity: 0.9 }}>
          Explore expert insights, hiring strategies, skill-based recruitment
          guides, and AI-powered hiring knowledge designed for HR teams,
          recruiters, and job seekers.
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Featured Resources */}
        <Typography
          variant="h4"
          align="center"
          sx={{ color: themeColor, fontWeight: 700, mb: 4 }}
        >
          Popular Downloads & Guides
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              icon: (
                <PictureAsPdfIcon sx={{ fontSize: 50, color: themeColor }} />
              ),
              title: "AI Recruiting Handbook",
              desc: "A step-by-step guide to adopting AI hiring workflows efficiently.",
              btn: "Download PDF",
            },
            {
              icon: (
                <LibraryBooksIcon sx={{ fontSize: 50, color: themeColor }} />
              ),
              title: "Skill-Based Hiring Framework",
              desc: "Learn how to evaluate real talent beyond job titles.",
              btn: "Download Guide",
            },
            {
              icon: (
                <TipsAndUpdatesIcon sx={{ fontSize: 50, color: themeColor }} />
              ),
              title: "Candidate Experience Playbook",
              desc: "Best practices to improve engagement and hiring success.",
              btn: "Download Report",
            },
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: "16px",
                  borderTop: `6px solid ${themeColor}`,
                  transition: "0.3s",
                  height: "100%",
                  "&:hover": { transform: "translateY(-6px)", boxShadow: 6 },
                }}
              >
                {item.icon}
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ opacity: 0.7, mt: 1 }}>
                  {item.desc}
                </Typography>
                <Button
                  variant="outlined"
                  sx={{
                    mt: 3,
                    borderColor: themeColor,
                    color: themeColor,
                    borderRadius: "10px",
                    "&:hover": { background: "#e3f2fa" },
                  }}
                  startIcon={<ArticleIcon />}
                >
                  {item.btn}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Blogs Section */}
        <Box sx={{ mt: 10 }}>
          <Typography
            variant="h4"
            align="center"
            sx={{ color: themeColor, fontWeight: 700, mb: 4 }}
          >
            Latest Articles & Insights
          </Typography>

          <Grid container spacing={4}>
            {[
              "Why traditional hiring is failing",
              "How AI detects role-ready candidates",
              "Skill-first hiring vs resume-first hiring",
              "Top 10 recruitment automation strategies",
            ].map((title, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    cursor: "pointer",
                    "&:hover": { background: "#f2faff" },
                  }}
                >
                  <ArticleIcon sx={{ fontSize: 35, color: themeColor }} />
                  <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Video Tutorials */}
        <Box sx={{ mt: 10 }}>
          <Typography
            variant="h4"
            align="center"
            sx={{ color: themeColor, fontWeight: 700, mb: 4 }}
          >
            Video Tutorials & Walkthroughs
          </Typography>

          <Grid container spacing={4}>
            {[1, 2, 3].map((_, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper
                  sx={{
                    height: 180,
                    borderRadius: "16px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "#eef7ff",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.85 },
                  }}
                >
                  <OndemandVideoIcon sx={{ fontSize: 70, color: themeColor }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <Button
              variant="contained"
              startIcon={<RocketLaunchIcon />}
              sx={{
                px: 6,
                py: 1.5,
                background: themeColor,
                fontSize: "16px",
                borderRadius: "12px",
                fontWeight: 700,
                "&:hover": { background: "#164964" },
              }}
              onClick={() => navigate("/register")}
            >
              Start Using eBench.ai
            </Button>
          </motion.div>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default Resources;
